import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { EscalationReason } from "@/types/agent_escalation_reason"

// Single row for the escalation reasons index, laid out as a grid cell set
// matching the per-section column header. Inline-edits name (input) and
// description (textarea) on click; blur or Enter saves, Esc cancels. Action
// buttons vary by status — caller wires only the handlers that apply.
//
// Grid columns (mirrored on the section headers):
//   24px placeholder | 1fr name+key | 2fr description | 80px used | 200px provenance | 280px actions

export const ESCALATION_GRID_COLS =
  "grid grid-cols-[24px_minmax(0,1fr)_minmax(0,2fr)_80px_200px_280px] items-center gap-3"

type Props = {
  reason: EscalationReason
  onApprove?: () => void
  onMerge?: () => void
  onArchive?: () => void
  onRestore?: () => void
  onRename?: (name: string) => void
  onEditDescription?: (description: string) => void
}

export function EscalationReasonRow({
  reason,
  onApprove,
  onMerge,
  onArchive,
  onRestore,
  onRename,
  onEditDescription,
}: Props) {
  const [editingName, setEditingName] = useState(false)
  const [editingDesc, setEditingDesc] = useState(false)
  const [draftName, setDraftName] = useState(reason.name)
  const [draftDesc, setDraftDesc] = useState(reason.description)

  const nameRef = useRef<HTMLInputElement | null>(null)
  const descRef = useRef<HTMLTextAreaElement | null>(null)

  // Keep local drafts in sync if the row updates from a server refetch.
  useEffect(() => { setDraftName(reason.name) }, [reason.name])
  useEffect(() => { setDraftDesc(reason.description) }, [reason.description])

  useEffect(() => { if (editingName) nameRef.current?.focus() }, [editingName])
  useEffect(() => { if (editingDesc) descRef.current?.focus() }, [editingDesc])

  const commitName = () => {
    const next = draftName.trim()
    setEditingName(false)
    if (!next || next === reason.name) {
      setDraftName(reason.name)
      return
    }
    onRename?.(next)
  }

  const commitDesc = () => {
    const next = draftDesc.trim()
    setEditingDesc(false)
    if (next === reason.description) return
    onEditDescription?.(next)
  }

  const cancelName = () => { setDraftName(reason.name); setEditingName(false) }
  const cancelDesc = () => { setDraftDesc(reason.description); setEditingDesc(false) }

  const provenanceText = useMemo(() => {
    if (reason.status === "archived") {
      return `archived ${fmtCompactRelative(reason.updatedAt)}`
    }
    if (reason.status === "pending") {
      return `proposed by classifier · ${fmtCompactRelative(reason.createdAt)}`
    }
    // live
    if (reason.provenance === "human" && reason.createdByName) {
      return `by ${reason.createdByName} · ${fmtCompactRelative(reason.createdAt)}`
    }
    return `classifier · ${fmtCompactRelative(reason.createdAt)}`
  }, [reason.status, reason.provenance, reason.createdByName, reason.createdAt, reason.updatedAt])

  return (
    <div
      className={cn(
        ESCALATION_GRID_COLS,
        "border-b border-border/60 px-4 py-3 last:border-b-0 hover:bg-white/[0.02]",
      )}
    >
      {/* Placeholder for future drag-handle / bulk-select */}
      <div />

      {/* Name + key */}
      <div className="min-w-0">
        {editingName ? (
          <input
            ref={nameRef}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitName() }
              if (e.key === "Escape") { e.preventDefault(); cancelName() }
            }}
            className="block w-full rounded-control border border-brand-red/50 bg-white/5 px-2 py-1 text-sm font-medium text-app-text outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => onRename && setEditingName(true)}
            className={cn(
              "block w-full truncate text-left text-sm font-medium text-app-text",
              onRename && "cursor-text rounded hover:bg-white/5",
            )}
            title={onRename ? "Click to rename" : undefined}
          >
            {reason.name}
          </button>
        )}
        <code className="mt-0.5 block truncate font-mono text-[11px] text-app-muted">
          {reason.key}
        </code>
      </div>

      {/* Description */}
      <div className="min-w-0">
        {editingDesc ? (
          <textarea
            ref={descRef}
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitDesc() }
              if (e.key === "Escape") { e.preventDefault(); cancelDesc() }
            }}
            rows={3}
            className="w-full resize-y rounded-control border border-brand-red/50 bg-white/5 px-2 py-1 text-sm text-app-text outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => onEditDescription && setEditingDesc(true)}
            className={cn(
              "block w-full whitespace-pre-wrap text-left text-xs text-app-muted",
              onEditDescription && "cursor-text rounded hover:bg-white/5",
            )}
            title={onEditDescription ? "Click to edit description" : undefined}
          >
            {reason.description || (onEditDescription ? "Add a description…" : "—")}
          </button>
        )}
      </div>

      {/* Used */}
      <div className="text-sm tabular-nums text-app-text">{reason.timesUsed}</div>

      {/* Provenance */}
      <div className="truncate text-xs text-app-muted" title={provenanceText}>
        {provenanceText}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Unclassified is the protected catch-all — archiving/merging it
            would break the synchronous-default invariant on the Escalate
            tool. Disable both with an explanatory tooltip. */}
        {reason.key === "unclassified" ? (
          <span className="text-[11px] italic text-app-muted">protected catch-all</span>
        ) : (
          <>
            {onApprove && (
              <Button size="sm" variant="red" onClick={onApprove}>Approve</Button>
            )}
            {onMerge && (
              <Button size="sm" variant="ghost" onClick={onMerge}>Merge into…</Button>
            )}
            {onArchive && (
              <Button size="sm" variant="ghost" onClick={onArchive}>Archive</Button>
            )}
            {onRestore && (
              <Button size="sm" variant="ghost" onClick={onRestore}>Restore</Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
